<template>
<div class="chart-card">
    <div class="chart-header">
        <div class="chart-icon">€</div>
        <div class="chart-title">Rolling Affiliation Revenues</div>
        <div class="chart-totals">
            <span class="chart-total">Settled: {{ totalEarnings }}</span>
            <span class="chart-total pending" v-if="hasProjections">Pending: {{ totalProjected }}</span>
        </div>
    </div>

    <div class="chart-legend" v-if="hasProjections">
        <span class="legend-item"><span class="legend-color settled"></span> Settled</span>
        <span class="legend-item"><span class="legend-color pending"></span> Pending</span>
    </div>

    <GChart type="ColumnChart" :data="chartData" :options="columnChartOptions" />

</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { GChart } from 'vue-google-charts'

const singleSeriesHeader = [
    'Month',
    'Settled',
    { role: 'tooltip', p: { html: true } },
]

const stackedHeader = [
    'Month',
    'Settled',
    { role: 'tooltip', p: { html: true } },
    'Pending',
    { role: 'tooltip', p: { html: true } },
]

const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]

const euroFormat = Intl.NumberFormat('en-DE', {
    style: 'currency',
    currency: 'EUR'
});

const createTooltip = ({ month, year, amount }, pending = false) => {
    const label = pending ? '<em>Pending</em><br/>' : ''
    return `<div class="chart-tooltip-content">
  ${label}<small>${month}/${year}</small> <span>${euroFormat.format(amount)}</span>
  </div>`
}


export default {
    name: 'RollingEarningsGraph',
    components: {
        GChart,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        baseChartOptions: {
            animation: {
                duration: 500,
                easing: 'linear',
                startup: true,
            },
            chartArea: {
                backgroundColor: 'transparent',
                width: '85%',
                height: '90%',
            },
            legend: {
                position: 'none',
            },
            bars: 'vertical',
            bar: { groupWidth: '88%' },
            vAxis: {
                baselineColor: 'transparent',
                gridlines: {
                    color: '#ededed',
                    count: 4,
                },
                format: '# €',
                textStyle: {
                    color: '#a2a2a2',
                },
            },
            hAxis: {
                baselineColor: 'transparent',
                textStyle: {
                    color: '#a2a2a2',
                },
            },
            fontName: 'Averta',
            fontSize: '14',
            height: 400,
            backgroundColor: 'transparent',
            tooltip: { isHtml: true },
        },
    }),
    computed: {
        ...mapState({
            rollingMonthlyEarnings: state => state.statisticsModule.rollingMonthlyEarnings,
            projectedMonthlyEarnings: state => state.statisticsModule.projectedMonthlyEarnings,
        }),
        ...mapGetters({
            rollingEarningsTotal: 'statisticsModule/rollingEarningsTotal',
            projectedEarningsTotal: 'statisticsModule/projectedEarningsTotal',
        }),
        hasProjections() {
            return this.projectedMonthlyEarnings.some(entry => entry.amount > 0)
        },
        columnChartOptions() {
            return {
                ...this.baseChartOptions,
                isStacked: this.hasProjections,
                colors: this.hasProjections ? ['#01C3A7', '#F0AD4E'] : ['#01C3A7'],
            }
        },
        totalEarnings() {
            return euroFormat.format(this.rollingEarningsTotal)
        },
        totalProjected() {
            return euroFormat.format(this.projectedEarningsTotal)
        },
        chartData() {
            if (!this.hasProjections) {
                return this.rollingMonthlyEarnings.reduce((acc, entry) => {
                    return [
                        ...acc,
                        [
                            monthNames[entry.month - 1],
                            entry.amount || null,
                            createTooltip({ month: entry.month, year: entry.year, amount: entry.amount }),
                        ]
                    ]
                }, [singleSeriesHeader])
            }

            const monthsMap = new Map()

            this.rollingMonthlyEarnings.forEach(entry => {
                const key = `${entry.year}-${entry.month}`
                monthsMap.set(key, {
                    month: entry.month,
                    year: entry.year,
                    earned: entry.amount,
                    projected: 0,
                })
            })

            this.projectedMonthlyEarnings.forEach(entry => {
                const key = `${entry.year}-${entry.month}`
                if (monthsMap.has(key)) {
                    monthsMap.get(key).projected = entry.amount
                } else {
                    monthsMap.set(key, {
                        month: entry.month,
                        year: entry.year,
                        earned: 0,
                        projected: entry.amount,
                    })
                }
            })

            const sorted = [...monthsMap.values()].sort(
                (a, b) => a.year - b.year || a.month - b.month
            )

            return sorted.reduce((acc, entry) => {
                const earned = entry.earned || null
                const projected = entry.projected || null
                return [
                    ...acc,
                    [
                        monthNames[entry.month - 1],
                        earned,
                        createTooltip({ month: entry.month, year: entry.year, amount: entry.earned }),
                        projected,
                        createTooltip({ month: entry.month, year: entry.year, amount: entry.projected }, true),
                    ]
                ]
            }, [stackedHeader])
        }
    },
}
</script>

<style scoped>
.chart-totals {
    display: flex;
    gap: 1.5rem;
    align-items: center;
}

.chart-total.pending {
    color: #F0AD4E;
    font-style: italic;
}

.chart-legend {
    display: flex;
    gap: 1rem;
    padding: 0.25rem 1rem;
    font-size: 0.85rem;
    color: #666;
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
}

.legend-color.settled {
    background-color: #01C3A7;
}

.legend-color.pending {
    background-color: #F0AD4E;
}
</style>